import * as XLSX from 'xlsx';


// Expected structure from your reference file (ICEGATE data.xlsx)
const EXPECTED_TITLE_ROW = ["ICEGATE data"];
const EXPECTED_HEADERS = [
    "SL No",
    "SB No", 
    "SB Date",
    "Port Code",
    "Rosctel Amount",
    "RodTep Amount"
];

const validateExcelHeaders = async (file) => {
    try {
        // Read the file
        const arrayBuffer = await file.arrayBuffer();
        
        // Parse with SheetJS (you'll need to import * as XLSX from 'xlsx')
        const workbook = XLSX.read(arrayBuffer, {
            type: 'buffer',
            cellDates: true,
            cellNF: true
        });
        
        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        if (!worksheet) {
            return 'Excel file appears to be empty or corrupted';
        }
        
        // Convert to JSON array to easily access rows
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            header: 1, 
            defval: '',
            raw: false  // This ensures we get string values
        });
        
        if (jsonData.length < 2) {
            return 'Excel file must have at least 2 rows (title row and header row)';
        }
        
        // Validate Row 1 (Title Row)
        const titleRow = jsonData[0].map(cell => (cell || '').toString().trim()).filter(cell => cell !== '');
        
        if (titleRow.length !== EXPECTED_TITLE_ROW.length) {
            return `Row 1 validation failed: Title not as Expected.Expected: ${EXPECTED_TITLE_ROW}`;
        }
        
        // Check title row content matches exactly
        for (let i = 0; i < EXPECTED_TITLE_ROW.length; i++) {
            const expected = EXPECTED_TITLE_ROW[i].trim();
            const actual = (titleRow[i] || '').trim();
            
            if (expected !== actual) {
                return `Row 1 validation failed: Expected "${expected}", but found "${actual}"`;
            }
        }
        
        // Validate Row 2 (Header Row)
        const headerRow = jsonData[1].map(cell => (cell || '').toString().trim()).filter(cell => cell !== '');
        
        if (headerRow.length !== EXPECTED_HEADERS.length) {
            return `Row 2 validation failed: Expected ${EXPECTED_HEADERS.length} headers, but found ${headerRow.length}`;
        }
        
        // Check each header matches exactly (case-sensitive)
        const mismatches = [];
        for (let i = 0; i < EXPECTED_HEADERS.length; i++) {
            const expected = EXPECTED_HEADERS[i].trim();
            const actual = (headerRow[i] || '').trim();
            
            if (expected !== actual) {
                mismatches.push({
                    position: i + 1,
                    expected: expected,
                    actual: actual
                });
            }
        }
        
        if (mismatches.length > 0) {
            const mismatchDetails = mismatches.map(m => 
                `Column ${m.position}: expected "${m.expected}", got "${m.actual}"`
            ).join('; ');
            return `Row 2 validation failed: ${mismatchDetails}`;
        }
        
        return null; // All validations passed
        
    } catch (error) {
        console.error('Error validating Excel structure:', error);
        return 'Error reading Excel file. Please ensure it is a valid Excel file.';
    }
};

/**
 * Your existing file validation function (unchanged)
 */
const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const allowedExtensions = ['.xls', '.xlsx'];

    if (file.size > maxSize) {
        return 'File size must be less than 10MB';
    }

    if (!allowedTypes.includes(file.type) &&
        !allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext))) {
        return 'Only XLS and XLSX files are allowed';
    }

    return null;
};

/**
 * Combined validation function that validates both file format and headers
 * @param {File} file - The Excel file to validate
 * @returns {Promise<string|null>} - Returns error message or null if valid
 */
const validateFileAndHeaders = async (file) => {
    // First validate file format and size
    const fileError = validateFile(file);
    if (fileError) {
        return fileError;
    }
    
    // Then validate headers
    const headerError = await validateExcelHeaders(file);
    if (headerError) {
        return headerError;
    }
    
    return null; // All validations passed
};

// Export functions for use in your React component
export { validateFile, validateExcelHeaders, validateFileAndHeaders, EXPECTED_TITLE_ROW, EXPECTED_HEADERS };